import { withErrorHandling } from 'helpers/withErrorHandling';
import { AnalyticsBrowser } from './lib/segment';
import { SegmentUser, SegmentGroup } from './utils/trackingLocalStorage';
import { isTrackingActive } from 'src/tracking/utils/isTrackingActive';
import { normalizePath } from 'src/tracking/utils/normalizePath';
import {
  getAccount,
  getAccountId,
  getPath,
  getPreferredLanguage,
  getSubscriptionPlan,
  getURL,
  getVenue,
  getVenueGroupId,
  getVenueId,
  getVenueLanguageWithoutRegion,
  getVenues,
} from 'src/tracking/utils/context';

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    analytics: any;
  }
}

export const init = withErrorHandling(() => {
  const writeKey = App.segmentWriteKey;
  if (!writeKey) {
    throw Error('No segment write key found, analytics will not be initialized');
  }

  AnalyticsBrowser.load({ writeKey });
});

export const clearIdentify = withErrorHandling(() => SegmentUser.clearIdentified());

export const identify = withErrorHandling(() => {
  if (!isTrackingActive()) {
    return;
  }

  const userId = getAccountId();
  if (SegmentUser.isAlreadyIdentified({ userId })) {
    return;
  }

  const account = getAccount();
  const venue = getVenue();
  const associatedVenues = getVenues();

  const properties = {
    email: account.emailAddress,
    country: venue.countryCode,
    venue_id_associated: (associatedVenues ?? []).map(({ id }) => id),
    name: account.employeeName,
    venue_id: getVenueId(),
    role: account.employeeRoleCode,
    preferred_language: getPreferredLanguage(),
    venue_language: getVenueLanguageWithoutRegion(),
    subscription_plan: getSubscriptionPlan(),
  };

  window.analytics.identify(userId, properties);

  SegmentUser.markIdentified({ userId });
});

export const page = withErrorHandling(() => {
  if (!isTrackingActive()) {
    return;
  }

  const path = getPath();
  const pageName = normalizePath(path);
  const venue = getVenue();

  const properties = {
    country: venue.countryCode,
    venue_id: getVenueId(),
    url: getURL(),
    path,
    is_masquerading: App.isMasquerading(),
    supplier_id: venue.supplierId,
    platform: 'web',
  };

  const context = {
    groupId: getVenueGroupId(),
  };

  window.analytics.page(pageName, properties, context);
});

export const clearGroup = withErrorHandling(() => SegmentGroup.clearTracked());

export const group = withErrorHandling(async () => {
  if (!isTrackingActive()) {
    return;
  }

  const venueId = getVenueGroupId();
  if (SegmentGroup.isAlreadyTracked({ venueId })) {
    return;
  }

  const venue = getVenue();
  const activesEmployees = await Wahanda.Cache.employees();

  const properties = {
    address: {
      postal_code: venue.postalRef,
      street: venue.addressLines,
      country: venue.countryCode,
    },
    created_at: venue.created,
    name: venue.name,
    supplier_id: venue.supplierId,
    type: venue.type,
    language: getVenueLanguageWithoutRegion(),
    employees: activesEmployees.length,
  };

  window.analytics.group(venueId, properties);

  SegmentGroup.markTracked({ venueId });
});

/**
 * Segment accepts nested objects as event properties, and the Menu edit events
 * send `old_values` / `new_values` maps and a `pricing_options` list of objects
 * (THY-2397), so the property type allows nested objects and lists of them.
 */
export type TrackingPropertyValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | string[]
  | number[]
  | { [key: string]: TrackingPropertyValue }
  | { [key: string]: TrackingPropertyValue }[];

export type TrackingProperties = Record<string, TrackingPropertyValue>;

export const track = withErrorHandling((name: string, properties?: TrackingProperties) => {
  if (!isTrackingActive()) {
    return;
  }

  const context = {
    groupId: getVenueGroupId(),
  };

  window.analytics.track(
    name,
    {
      country: getVenue().countryCode,
      platform: 'web',
      venue_id: getVenueId(),
      is_masquerading: App.isMasquerading(),
      supplier_id: getVenue().supplierId,
      ...properties,
    },
    context,
  );
});
